import { useState } from 'react'
import { ApiError, accounts, session } from '../../lib/api'
import { localProblem } from './password'

/**
 * Changing your own password, wherever that is being asked for.
 *
 * Two screens ask. One is the takeover an account is held on until it replaces a password somebody
 * else chose; the other is a card on Settings that anybody can open whenever they like. They differ
 * only in the words around them and in what happens afterwards, so the fields, the rules and the
 * refusal live here once.
 *
 * **The DOM contract is load-bearing and deliberately unchanged.** `tests/e2e/specs/accounts.spec.ts`
 * fills `#current-password`, `#new-password` and `#confirm-password` and submits through
 * `[data-testid="change-password-form"]`. Those four strings moved files and must not change while
 * doing so.
 */
export function PasswordForm({
  submitLabel,
  savingLabel,
  onChanged
}: {
  submitLabel: string
  savingLabel: string
  onChanged: () => void
}) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const email = session.identity()?.displayName ?? ''

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Checked here rather than at the server, because the server is not told the confirmation and
    // could not check it. Everything else is the server's answer, repeated verbatim.
    const local = localProblem(newPassword, confirmation)
    if (local) {
      setProblem(local)
      return
    }

    setSaving(true)
    setProblem(null)
    try {
      await accounts.changeOwnPassword(currentPassword, newPassword)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmation('')
      onChanged()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The password could not be changed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} data-testid="change-password-form">
      {/*
        Hidden, but present and filled in: a password manager cannot offer to save a new password
        unless it can tell which account the password belongs to, and a change form with no
        username field is one it silently declines to help with.
      */}
      <input type="text" name="username" autoComplete="username" value={email} readOnly hidden />

      <label htmlFor="current-password">Current password</label>
      <input
        id="current-password"
        type="password"
        autoComplete="current-password"
        value={currentPassword}
        onChange={(event) => setCurrentPassword(event.target.value)}
        required
      />

      <label htmlFor="new-password">New password</label>
      {/*
        The rules stated rather than discovered by failing. `PasswordHashing.Problems` is still the
        only thing that decides - this is the same sentence said before the refusal rather than
        instead of it, so the two cannot disagree about the outcome, only about the timing.
      */}
      <p className="hint" id="new-password-hint">
        At least twelve characters, and at least one that is not a letter.
      </p>
      <input
        id="new-password"
        type="password"
        autoComplete="new-password"
        aria-describedby="new-password-hint"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        required
      />

      <label htmlFor="confirm-password">New password again</label>
      <input
        id="confirm-password"
        type="password"
        autoComplete="new-password"
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        required
      />

      {problem && (
        <p className="error" role="alert" data-testid="change-password-error">
          {problem}
        </p>
      )}

      <button type="submit" disabled={saving}>
        {saving ? savingLabel : submitLabel}
      </button>
    </form>
  )
}
